'use client';

import { AnimatedContainer } from '@/components/ui/animated-container';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CodeBlock } from '@/components/ui/code-block';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useAnimation } from '@/contexts/AnimationContext';
import { useSession } from 'next-auth/react';
import {
  clampPlaygroundTimeoutMs,
  PLAYGROUND_DEFAULT_TIMEOUT_MS,
  PLAYGROUND_MAX_TIMEOUT_MS,
  resolvePlaygroundUpstreamTimeoutMs,
} from '@/lib/docs-playground';
import { CheckIcon, CopyIcon } from 'lucide-react';
import { PublicLink } from '@/components/PublicLink';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PageHero,
  PageIntro,
  PageSection,
  SectionPill,
  SurfacePanel,
} from '@/components/site/page-primitives';

type Mode = 'sync' | 'async';

type TocItem = {
  id: string;
  label: string;
};

const DEFAULT_IDEMPOTENCY_KEY = '123e4567-e89b-12d3-a456-426614174000';
const MOBILE_SELECT_CLASS_NAME =
  'flex h-11 w-full rounded-2xl border border-[color:var(--site-border-soft)] bg-[var(--site-surface-strong)] px-4 py-2 text-sm shadow-[var(--site-shadow-soft)] focus:outline-none focus:ring-2 focus:ring-ring';

type NativeSelectOption = {
  value: string;
  label: string;
};

function NativeSelect({
  value,
  onValueChange,
  options,
  ariaLabel,
}: {
  value: string;
  onValueChange: (value: string) => void;
  options: NativeSelectOption[];
  ariaLabel: string;
}) {
  return (
    <select
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
      className={MOBILE_SELECT_CLASS_NAME}
      aria-label={ariaLabel}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function createIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function ApiDocumentationPage() {
  const { enableAnimations } = useAnimation();
  const { status } = useSession();
  const isAuthenticated = status === 'authenticated';

  const [copied, setCopied] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('async');
  const [tocJumpId, setTocJumpId] = useState<string>('top');
  const [navigationAnnouncement, setNavigationAnnouncement] = useState<string>('');
  const [realApiKey, setRealApiKey] = useState<string | null>(null);
  const [apiKeyFetchState, setApiKeyFetchState] = useState<'idle' | 'loading' | 'loaded' | 'none' | 'error'>('idle');

  const [syncExamplesTab, setSyncExamplesTab] = useState<'python' | 'javascript' | 'curl'>('python');
  const [asyncExamplesTab, setAsyncExamplesTab] = useState<'start' | 'poll' | 'websocket'>('start');

  const [syncResponsesTab, setSyncResponsesTab] = useState<'success' | 'error'>('success');
  const [asyncResponsesTab, setAsyncResponsesTab] = useState<'start' | 'status'>('start');

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const apiBaseUrl = process.env.NEXT_PUBLIC_API_URL || 'https://www.s-shot.ru';
  const wsBaseUrl = (process.env.NEXT_PUBLIC_WS_URL || 'wss://www.s-shot.ru')
    .replace('ws://', 'wss://')
    .replace('http://', 'ws://');

  const envAuthHeaderValue = 'Authorization: Bearer $API_KEY';

  const maskedRealApiKey = useMemo(() => {
    if (!realApiKey) return null;
    if (realApiKey.length <= 12) return `${realApiKey.slice(0, 2)}…${realApiKey.slice(-2)}`;
    return `${realApiKey.slice(0, 10)}…${realApiKey.slice(-5)}`;
  }, [realApiKey]);

  const syncRequestBody = `{
  "url": "https://example.com/page",              // required
  "prompt": "Extract product name & price",    // required, max 2048 chars
  "schema": { ... },                           // optional JSON Schema
  "schema_id": "schema_abc",                   // optional; stable schema family identifier
  "enforce_schema": false,                     // optional; fail on mismatch when true
  "language": "en",                            // optional
  "options": {
    "wait_ms": 0,                              // 0–60000
    "wait_until": "load",                      // load | domcontentloaded | networkidle
    "wait_for_selector": "#price",             // optional
    "headers": { "User-Agent": "..." },        // optional
    "cookies": "name=value; ...",              // optional
    "timeout_ms": 30000,                       // 1–120000
    "screenshot": false                        // optional
  }
}`;

  const asyncRequestBody = `{
  "url": "https://example.com/page",              // required
  "prompt": "Extract product name & price",    // required, max 2048 chars
  "options": {
    "timeout_ms": 30000,                       // 1–120000
    "screenshot": false                        // optional
  }
}`;

  const syncSuccessResponse = `{
  "schema_id": "schema_abc",
  "data": { "name": "Acme Anvil", "price": 199.99 },
  "schema_used": { ... },
  "validation_errors": [],
  "duration_ms": 1820,
  "screenshot_url": "https://cdn.example.com/runs/example.png",
  "detected_language": "en"
}`;

  const syncValidationErrorResponse = `{
  "error": "validation_error",
  "message": "Validation failed against provided schema",
  "step": "schema_validation",
  "run_id": "run_456",
  "retryable": false,
  "validation_errors": [
    { "path": "/price", "message": "expected number" }
  ]
}`;

  const asyncStartResponse = `{
  "run_id": "run_234b534e-23e2-448c-9043-bc816eaa391a",
  "status_url": "/v1.0/runs/run_234b534e-23e2-448c-9043-bc816eaa391a",
  "websocket": "/ws/run_234b534e-23e2-448c-9043-bc816eaa391a?token=…",
  "websocket_token": "…",
  "websocket_token_expires_at": 1733421457
}`;

  const asyncRunStatusResponse = `{
  "run_id": "run_234b534e-23e2-448c-9043-bc816eaa391a",
  "status": "queued|running|success|completed|completed_partial|failed|cancelled",
  "step": "download|semantic|parse",
  "schema_id": "schema_abc",
  "data": { ... },
  "error": null
}`;

  const wsMessageTypes = `// Message types
{ "type": "status", "run_id": "...", "status": "queued|running|success|completed|completed_partial|failed|cancelled", "step": "download|semantic|parse", "error": null }
{ "type": "started", "run_id": "...", "request_id": "..." }
{ "type": "progress", "run_id": "...", "step": "download|semantic|parse" }
{ "type": "result", "run_id": "...", "json": { ... }, "screenshot_url": "..." }
{ "type": "error", "run_id": "...", "step": "download", "message": "...", "retryable": false }
{ "type": "done", "run_id": "...", "status": "success|completed|completed_partial|failed|cancelled" }
{ "type": "ping", "ts": 1733421457 }`;

  useEffect(() => {
    let isCurrent = true;

    const loadApiKey = async () => {
      if (!isAuthenticated) {
        setRealApiKey(null);
        setApiKeyFetchState('idle');
        return;
      }

      setApiKeyFetchState('loading');
      try {
        const response = await fetch('/api/user/api-keys', { cache: 'no-store' });
        if (!response.ok) {
          if (isCurrent) setApiKeyFetchState('error');
          return;
        }
        const data = await response.json();
        const apiKeys = Array.isArray(data?.apiKeys) ? data.apiKeys : [];

        const activeKey = apiKeys.find((k: any) => k?.isActive && typeof k?.key === 'string') ?? null;
        if (isCurrent) {
          setRealApiKey(activeKey?.key ?? null);
          setApiKeyFetchState(activeKey?.key ? 'loaded' : 'none');
        }
      } catch {
        if (isCurrent) setApiKeyFetchState('error');
      }
    };

    loadApiKey();
    return () => {
      isCurrent = false;
    };
  }, [isAuthenticated]);

  const curlSync = `curl -X POST ${apiBaseUrl}/v1.0/extract \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer $API_KEY' \\
  -H 'Idempotency-Key: 123e4567-e89b-12d3-a456-426614174000' \\
  -d '{
    "url": "https://www.example.com/product/1234",
    "prompt": "technical specifications",
    "options": {
      "wait_ms": 3000,
      "wait_until": "load",
      "wait_for_selector": "#price",
      "headers": {"User-Agent": "Custom UA"},
      "cookies": "name=value; ...",
      "timeout_ms": 30000,
      "screenshot": true
    }
  }'`;

  const curlAsyncStart = `curl -X POST ${apiBaseUrl}/v1.0/extract-async \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer $API_KEY' \\
  -H 'Idempotency-Key: 123e4567-e89b-12d3-a456-426614174000' \\
  -d '{
    "url": "https://www.example.com/product/1234",
    "prompt": "technical specifications",
    "options": { "screenshot": true, "timeout_ms": 30000 }
  }'`;

  const curlAsyncPoll = `curl -X GET ${apiBaseUrl}/v1.0/runs/run_234b534e-23e2-448c-9043-bc816eaa391a \\
  -H 'Authorization: Bearer $API_KEY'`;

  const javascriptExample = `const res = await fetch('${apiBaseUrl}/v1.0/extract', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + process.env.API_KEY,
    'Idempotency-Key': crypto.randomUUID()
  },
  body: JSON.stringify({
    url: 'https://www.example.com/product/1234',
    prompt: 'technical specifications',
    enforce_schema: false,
    options: { wait_ms: 0, wait_until: 'load', screenshot: false, timeout_ms: 30000 }
  })
});
const data = await res.json();
console.log(data.schema_id, data.data);`;

  const pythonExample = `import os, uuid, requests

payload = {
    "url": "https://www.example.com/product/1234",
    "prompt": "technical specifications",
    "options": {
        "wait_ms": 0,
        "wait_until": "load",
        "timeout_ms": 30000,
        "screenshot": False
    }
}

resp = requests.post(
    "${apiBaseUrl}/v1.0/extract",
    headers={
        "Authorization": f"Bearer {os.environ['API_KEY']}",
        "Content-Type": "application/json",
        "Idempotency-Key": str(uuid.uuid4())
    },
    json=payload,
    timeout=65
)
print(resp.status_code, resp.json())`;

  const wsExample = `// 1) Start an async run
const start = {
  run_id: 'run_234b534e-23e2-448c-9043-bc816eaa391a',
  websocket: '/ws/run_234b534e-23e2-448c-9043-bc816eaa391a?token=example',
  websocket_token: 'example',
  websocket_token_expires_at: 1733421457
};

// 2) Connect (API keys should NOT appear in the WebSocket URL)
const wsUrl = start.websocket.startsWith('ws') ? start.websocket : '${wsBaseUrl}' + start.websocket;
const socket = new WebSocket(wsUrl);

socket.onmessage = (event) => {
  const msg = JSON.parse(event.data);
  if (msg.type === 'result') {
    console.log('Extracted data:', msg.json);
  }
  if (msg.type === 'error') {
    console.error('Extraction error:', msg.message);
  }
};`;

	  const tocItems = useMemo<TocItem[]>(() => {
	    const items: TocItem[] = [
	      { id: 'top', label: 'Обзор' },
	      { id: 'quickstart', label: 'Быстрый старт' },
      { id: 'download-workflow', label: 'Режим `download`' },
	      { id: 'playground', label: 'Песочница' },
	      { id: 'authentication', label: 'Аутентификация' },
	    ];

    if (mode === 'sync') {
      items.push(
	        { id: 'sync-endpoint', label: 'Синхронный метод' },
        { id: 'sync-request-body', label: 'Тело запроса' },
        { id: 'sync-responses', label: 'Ответы' },
        { id: 'sync-examples', label: 'Примеры кода' },
      );
    } else {
      items.push(
	        { id: 'async-endpoint', label: 'Асинхронный метод' },
        { id: 'async-request-body', label: 'Тело запроса' },
        { id: 'async-responses', label: 'Ответы' },
        { id: 'async-websocket', label: 'WebSocket' },
        { id: 'async-examples', label: 'Примеры кода' },
      );
    }

    items.push({ id: 'limits', label: 'Лимиты и оплата' });
    return items;
  }, [mode]);

  const resolveSectionLabel = useCallback(
    (id: string) => tocItems.find((item) => item.id === id)?.label ?? id,
    [tocItems],
  );

  const focusHashTarget = useCallback((element: HTMLElement) => {
    const focusTarget = (element.closest('section,article,[role="region"]') as HTMLElement | null) ?? element;
    if (!focusTarget.hasAttribute('tabindex')) {
      focusTarget.setAttribute('tabindex', '-1');
    }
    focusTarget.focus({ preventScroll: true });
  }, []);

  const scrollToId = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => focusHashTarget(el), 0);
    setNavigationAnnouncement(`Открыт раздел: ${resolveSectionLabel(id)}`);
  }, [focusHashTarget, resolveSectionLabel]);

  const navigateTo = useCallback((id: string) => (e?: { preventDefault?: () => void }) => {
    e?.preventDefault?.();
    window.history.replaceState(null, '', `#${id}`);
    setTocJumpId(id);
    scrollToId(id);
  }, [scrollToId]);

  const setModeAndScroll = useCallback((nextMode: Mode, targetId: string) => {
    setMode(nextMode);
    window.history.replaceState(null, '', `#${targetId}`);
    setTocJumpId(targetId);
    setNavigationAnnouncement(
      `Переключен режим на ${nextMode === 'sync' ? 'синхронный' : 'асинхронный'}. ${resolveSectionLabel(targetId)}`,
    );
    setTimeout(() => scrollToId(targetId), 0);
  }, [resolveSectionLabel, scrollToId]);

  useEffect(() => {
    const applyHash = () => {
      const raw = window.location.hash || '';
      const id = raw.replace('#', '');
      if (!id) return;
      if (id.startsWith('async-')) setMode('async');
      if (id.startsWith('sync-')) setMode('sync');
      setTocJumpId(id);
      setTimeout(() => scrollToId(id), 0);
    };

    applyHash();
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, [scrollToId]);

  useEffect(() => {
    setTocJumpId('top');
  }, [mode]);

  return (
    <>
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {navigationAnnouncement}
      </div>
      <PageHero>
        <div
          id="top"
          className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_16rem] lg:items-start lg:gap-8"
        >
          <PageIntro
            eyebrow="Справочник"
            title="Документация API"
            description="Документация по API S-SHOT на каноническом хосте `www.s-shot.ru`. Публичная песочница выполняет только асинхронные запросы; синхронные и `download`-сценарии остаются справочными примерами."
            actions={
              <div className="flex flex-wrap items-center gap-3">
                <SectionPill>Текущий режим: {mode === 'sync' ? 'синхронный' : 'асинхронный'}</SectionPill>
                <Button
                  size="sm"
                  variant={mode === 'sync' ? 'default' : 'outline'}
                  onClick={() => setModeAndScroll('sync', 'sync-endpoint')}
                >
                  Синхронно
                </Button>
                <Button
                  size="sm"
                  variant={mode === 'async' ? 'default' : 'outline'}
                  onClick={() => setModeAndScroll('async', 'async-endpoint')}
                >
                  Асинхронно
                </Button>
              </div>
            }
          >
            {!isAuthenticated ? (
              <div className="flex flex-wrap items-center gap-1 text-sm text-[var(--site-accent-text)]">
                <span className="font-medium">Нужен API-ключ?</span>
                <PublicLink href="/signup" className="hover:underline">
                  Зарегистрируйтесь
                </PublicLink>
                <span>или</span>
                <PublicLink href="/login" className="hover:underline">
                  войдите
                </PublicLink>
                <span>чтобы начать вызывать API.</span>
              </div>
            ) : null}
          </PageIntro>

          <SurfacePanel className="hidden self-start gap-2 p-3.5 lg:flex lg:w-[16rem]">
            <div className="space-y-1">
              <div className="text-sm font-semibold text-[var(--site-ink-strong)]">
                На этой странице
              </div>
              <p className="text-xs text-[var(--site-ink-muted)]">
                Сейчас показаны разделы для {mode === 'sync' ? 'синхронного' : 'асинхронного'} режима.
              </p>
            </div>
            <nav className="flex flex-col gap-2">
              {tocItems.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={navigateTo(item.id)}
                  className="flex min-h-11 items-center rounded-xl px-3 py-2 text-sm text-[var(--site-ink)] transition-colors hover:bg-white/55 hover:text-[var(--site-ink-strong)]"
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </SurfacePanel>
        </div>
      </PageHero>
      <PageSection className="gap-8 pt-2 sm:pt-3">
        <AnimatedContainer
          animation={enableAnimations ? 'fadeIn' : 'none'}
          className="w-full"
        >
          <div className="space-y-8">
                <section id="quickstart" className="space-y-4 scroll-mt-24">
                  <h2 className="text-2xl font-semibold">Быстрый старт</h2>
                  <SurfacePanel className="gap-5">
                    <div className="space-y-1">
                      <h3 className="text-xl font-semibold text-[var(--site-ink-strong)]">
                        Базовые значения для копирования
                      </h3>
                      <p className="text-sm text-[var(--site-ink-muted)]">
	                        Начните с канонического базового URL `www.s-shot.ru`, заголовка авторизации и основных методов API.
                      </p>
                    </div>
                    <div className="space-y-3">
                      <CopyField
	                        label="Базовый URL"
                        value={apiBaseUrl}
                        copied={copied === 'base-url'}
                        onCopy={() => copyToClipboard(apiBaseUrl, 'base-url')}
                      />
                      <CopyField
	                        label="Заголовок Authorization"
                        value={envAuthHeaderValue}
                        copied={copied === 'auth-env'}
                        onCopy={() => copyToClipboard(envAuthHeaderValue, 'auth-env')}
                      />
                      {isAuthenticated && (
                        <>
                          {apiKeyFetchState === 'loaded' && realApiKey && maskedRealApiKey && (
                            <div className="grid gap-3 sm:grid-cols-2">
                              <CopyField
                                label="Мой API-ключ (замаскирован)"
                                value={maskedRealApiKey}
                                copied={copied === 'my-api-key'}
                                onCopy={() => copyToClipboard(realApiKey, 'my-api-key')}
                              />
                              <div className="rounded-md border bg-[var(--site-surface-inset)] p-3 flex items-start justify-between gap-4">
                                <div className="min-w-0">
                                  <div className="text-xs font-medium text-[var(--site-ink)]">Готовый заголовок Authorization</div>
                                  <div className="text-sm text-[var(--site-ink)]">
                                    Копирует полный заголовок и не показывает сам ключ на экране, чтобы его нельзя было случайно раскрыть.
                                  </div>
                                </div>
                                <button
                                  onClick={() => copyToClipboard(`Authorization: Bearer ${realApiKey}`, 'auth-real')}
                                  className="shrink-0 p-1 hover:bg-[color:rgba(23,38,59,0.12)] rounded"
                                  aria-label="Скопировать готовый заголовок Authorization"
                                >
                                  {copied === 'auth-real' ? <CheckIcon className="h-4 w-4 text-green-600" /> : <CopyIcon className="h-4 w-4" />}
                                </button>
                              </div>
                            </div>
                          )}
                          {apiKeyFetchState === 'none' && (
                            <div className="rounded-md border bg-[var(--site-surface-inset)] p-3 text-sm text-[var(--site-ink)]">
                              Активный API-ключ не найден. Создайте его в разделе{' '}
                              <PublicLink href="/api-keys" className="text-[var(--site-accent-text)] hover:underline">API-ключи</PublicLink>.
                            </div>
                          )}
                          {apiKeyFetchState === 'error' && (
                            <div className="rounded-md border bg-[var(--site-surface-inset)] p-3 text-sm text-[var(--site-ink)]">
                              Не удалось загрузить ваши API-ключи. Откройте{' '}
                              <PublicLink href="/api-keys" className="text-[var(--site-accent-text)] hover:underline">API-ключи</PublicLink>, чтобы скопировать один из них.
                            </div>
                          )}
                        </>
                      )}
                      <div className="grid gap-3 sm:grid-cols-3">
                        <CopyField
	                          label="Синхронный метод"
                          value={`POST ${apiBaseUrl}/v1.0/extract`}
                          copied={copied === 'endpoint-sync'}
                          onCopy={() => copyToClipboard(`${apiBaseUrl}/v1.0/extract`, 'endpoint-sync')}
                        />
                        <CopyField
	                          label="Асинхронный метод"
                          value={`POST ${apiBaseUrl}/v1.0/extract-async`}
                          copied={copied === 'endpoint-async'}
                          onCopy={() => copyToClipboard(`${apiBaseUrl}/v1.0/extract-async`, 'endpoint-async')}
                        />
                        <CopyField
	                        label="Метод download (справка)"
                          value={`POST ${apiBaseUrl}/v1.0/download`}
                          copied={copied === 'endpoint-download'}
                          onCopy={() => copyToClipboard(`${apiBaseUrl}/v1.0/download`, 'endpoint-download')}
                        />
                      </div>

                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pt-2">
                        <p className="text-sm text-[var(--site-ink)]">
	                          Синхронный режим и `download` описаны ниже как reference-only контракты. Публичная песочница на `www.s-shot.ru` выполняет только асинхронные запросы и последующий опрос статуса.
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setModeAndScroll('sync', 'sync-endpoint')}
                          >
	                            Открыть синхронный режим
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setModeAndScroll('async', 'async-endpoint')}
                          >
	                            Открыть асинхронный режим
                          </Button>
                        </div>
                      </div>
                    </div>
                  </SurfacePanel>
                </section>

                <section id="download-workflow" className="space-y-4 scroll-mt-24">
                  <div className="space-y-1">
                    <h2 className="text-2xl font-semibold">Режим download для многошаговых сценариев</h2>
                    <p className="text-sm text-[var(--site-ink)]">
	                      Используйте режим download, когда нужен захват файлов, извлечение с учетом сетевого поведения или многошаговый сценарий вместо обычного запроса с полем{' '}
	                      <code className="rounded bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5">prompt</code>.
                    </p>
                  </div>
                  <div className="grid gap-4 md:grid-cols-3">
                    <Card>
                      <CardHeader className="pb-3">
	                        <CardTitle className="text-lg">1. Используйте метод download</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3 text-sm text-[var(--site-ink)]">
	                        <p>Отправляйте тело запроса с полем <code className="rounded bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5">workflow</code> в метод download, а не в синхронный или асинхронный метод извлечения.</p>
                        <CopyField
	                          label="Метод download"
                          value={`POST ${apiBaseUrl}/v1.0/download`}
                          copied={copied === 'download-workflow-endpoint'}
                          onCopy={() => copyToClipboard(`${apiBaseUrl}/v1.0/download`, 'download-workflow-endpoint')}
                        />
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg">2. Добавьте трассировку запроса</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3 text-sm text-[var(--site-ink)]">
                        <p>
	                          Если в теле запроса есть объект <code className="rounded bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5">workflow</code>, добавляйте{' '}
	                          <code className="rounded bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5">x-request-id</code>, чтобы сервис мог связать запрос с сеансом download.
                        </p>
                        <CopyField
	                          label="Обязательный заголовок"
                          value="x-request-id: your-stable-request-id"
                          copied={copied === 'download-request-id'}
                          onCopy={() => copyToClipboard('x-request-id: your-stable-request-id', 'download-request-id')}
                        />
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-3">
	                        <CardTitle className="text-lg">3. Используйте code examples для download</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3 text-sm text-[var(--site-ink)]">
                        <p>
                          На публичном `www` режим `download` не исполняется интерактивно. Используйте эту секцию как source-of-truth для payload shape, обязательного <code className="rounded bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5">x-request-id</code> и код-примеров.
                        </p>
                          <Button variant="outline" size="sm" onClick={navigateTo('playground')}>
                            Перейти к песочнице
                          </Button>
                      </CardContent>
                    </Card>
                  </div>
                </section>

	                <section id="playground" className="space-y-4 scroll-mt-24">
	                  <div className="space-y-1">
	                    <h2 className="text-2xl font-semibold">Песочница</h2>
	                    <p className="text-sm text-[var(--site-ink)]">
		                      На публичном `www.s-shot.ru` песочница выполняет только <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">POST /v1.0/extract-async</code> и <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">GET /v1.0/runs/&#123;run_id&#125;</code>. Синхронный метод и `download` ниже остаются documentation-only примерами для интеграции.
	                    </p>
	                  </div>
	                  <ApiPlayground
	                    apiBaseUrl={apiBaseUrl}
	                    isAuthenticated={isAuthenticated}
	                    copied={copied}
	                    copyToClipboard={copyToClipboard}
	                  />
	                </section>

	                <section id="authentication" className="space-y-4 scroll-mt-24">
	                  <h2 className="text-2xl font-semibold">Аутентификация</h2>
	                  <p className="text-[var(--site-ink)]">Передавайте ключ как bearer token в каждом запросе.</p>
		                  <CopyField
		                    label="Заголовок"
	                    value={envAuthHeaderValue}
                    copied={copied === 'auth-env-2'}
                    onCopy={() => copyToClipboard(envAuthHeaderValue, 'auth-env-2')}
                  />
                  {isAuthenticated && apiKeyFetchState === 'loaded' && realApiKey && maskedRealApiKey && (
                    <div className="rounded-md border bg-[var(--site-surface-inset)] p-3 text-sm text-[var(--site-ink)]">
                      Ваш ключ выше показан в замаскированном виде. Используйте кнопки копирования или управляйте ключами в разделе{' '}
                      <PublicLink href="/api-keys" className="text-[var(--site-accent-text)] hover:underline">API-ключи</PublicLink>.
                    </div>
                  )}
                </section>

                <section className="space-y-4">
                  <div className="flex items-end justify-between gap-4">
                    <div className="space-y-1">
                      <h2 className="text-2xl font-semibold">Методы API</h2>
                      <p className="text-sm text-[var(--site-ink)]">Переключайтесь между синхронным и асинхронным режимами.</p>
                    </div>
                  </div>

                  <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)} className="w-full">
                    <div className="space-y-3">
                      <TabsList className="hidden sm:grid w-full grid-cols-2">
	                        <TabsTrigger value="sync">Синхронно</TabsTrigger>
	                        <TabsTrigger value="async">Асинхронно</TabsTrigger>
                      </TabsList>

                      <div className="sm:hidden">
                        <NativeSelect
                          value={mode}
                          onValueChange={(value) => setMode(value as Mode)}
                          ariaLabel="Выбрать режим API"
                          options={[
                            { value: 'sync', label: 'Синхронно' },
                            { value: 'async', label: 'Асинхронно' },
                          ]}
                        />
                      </div>

                      <div className="lg:hidden">
                        <NativeSelect
                          value={tocJumpId}
                          onValueChange={(id) => {
                            setTocJumpId(id);
                            navigateTo(id)();
                          }}
                          ariaLabel="Перейти к разделу документации"
                          options={tocItems.map((item) => ({ value: item.id, label: item.label }))}
                        />
                      </div>
                    </div>

                    <TabsContent value="sync" className="mt-6 space-y-10">
                      <section id="sync-endpoint" className="space-y-4 scroll-mt-24">
	                        <h3 className="text-2xl font-semibold">Синхронный метод</h3>
                        <div className="space-y-3">
	                          <EndpointRow method="POST" path="/v1.0/extract" description="Синхронное извлечение: JSON возвращается прямо в ответе." />
	                          <EndpointRow method="GET" path="/v1.0/schemas/{schema_id}" description="Возвращает версии схемы для выбранного семейства." />
                        </div>
                        <p className="text-[var(--site-ink)] text-sm">
	                          Необязательный заголовок:{' '}
                          <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">Idempotency-Key</code> для защиты от повторной отправки запроса в течение 24 часов.
                        </p>
                        <p className="text-[var(--site-ink)] text-sm">
                          <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">schema_id</code> задает группу совместимых схем, а изменения отслеживаются через{' '}
                          <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">schema_hash</code> и{' '}
                          <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">schema_version</code>. Описание{' '}
                          <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">OUTPUT_SCHEMA</code> в шаблоне ведется отдельно от жизненного цикла схем в API извлечения.
                        </p>
                      </section>

                      <section id="sync-request-body" className="space-y-3 scroll-mt-24">
                        <h3 className="text-2xl font-semibold">Тело запроса</h3>
                        <CodeBlock
                          code={syncRequestBody}
                          language="json"
                          copied={copied === 'sync-request'}
                          onCopy={() => copyToClipboard(syncRequestBody, 'sync-request')}
                          variant="light"
                          defaultWrap
                          showWrapToggle
                        />
                        <p className="text-[var(--site-ink)] text-sm">Лимит тела запроса: 1 МБ. URL на localhost и loopback отклоняются для SSRF-защиты.</p>
                      </section>

                      <section id="sync-responses" className="space-y-4 scroll-mt-24">
                        <h3 className="text-2xl font-semibold">Ответы</h3>
                        <Tabs value={syncResponsesTab} onValueChange={(v) => setSyncResponsesTab(v as typeof syncResponsesTab)}>
                          <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="success">Успех (200)</TabsTrigger>
                            <TabsTrigger value="error">Ошибка (400)</TabsTrigger>
                          </TabsList>
                          <TabsContent value="success" className="mt-4">
                            <CodeBlock
                              code={syncSuccessResponse}
                              language="json"
                              copied={copied === 'sync-success'}
                              onCopy={() => copyToClipboard(syncSuccessResponse, 'sync-success')}
                              variant="light"
                              defaultWrap
                              showWrapToggle
                            />
                          </TabsContent>
                          <TabsContent value="error" className="mt-4">
                            <CodeBlock
                              code={syncValidationErrorResponse}
                              language="json"
                              copied={copied === 'sync-error'}
                              onCopy={() => copyToClipboard(syncValidationErrorResponse, 'sync-error')}
                              variant="light"
                              defaultWrap
                              showWrapToggle
                            />
                          </TabsContent>
                        </Tabs>
                        <p className="text-[var(--site-ink)] text-sm">
                          Заголовки:{' '}
                          <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">x-api-version</code>,{' '}
                          <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">x-usage-plan</code>,{' '}
                          <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">x-request-id</code>.
                        </p>
                      </section>

                      <section id="sync-examples" className="space-y-4 scroll-mt-24">
                        <h3 className="text-2xl font-semibold">Примеры кода</h3>

                        <Tabs value={syncExamplesTab} onValueChange={(v) => setSyncExamplesTab(v as typeof syncExamplesTab)}>
                          <TabsList className="hidden sm:grid w-full grid-cols-3">
                            <TabsTrigger value="python">Python</TabsTrigger>
                            <TabsTrigger value="javascript">JavaScript</TabsTrigger>
                            <TabsTrigger value="curl">cURL</TabsTrigger>
                          </TabsList>

                          <div className="sm:hidden">
                            <NativeSelect
                              value={syncExamplesTab}
                              onValueChange={(value) => setSyncExamplesTab(value as typeof syncExamplesTab)}
                              ariaLabel="Выбрать пример синхронного запроса"
                              options={[
                                { value: 'python', label: 'Python' },
                                { value: 'javascript', label: 'JavaScript' },
                                { value: 'curl', label: 'cURL' },
                              ]}
                            />
                          </div>

                          <TabsContent value="javascript" className="mt-4">
                            <CodeBlock
                              code={javascriptExample}
                              language="javascript"
                              copied={copied === 'javascript'}
                              onCopy={() => copyToClipboard(javascriptExample, 'javascript')}
                              variant="dark"
                              collapsible
                              showWrapToggle
                            />
                          </TabsContent>

                          <TabsContent value="python" className="mt-4">
                            <CodeBlock
                              code={pythonExample}
                              language="python"
                              copied={copied === 'python'}
                              onCopy={() => copyToClipboard(pythonExample, 'python')}
                              variant="dark"
                              collapsible
                              showWrapToggle
                            />
                          </TabsContent>

                          <TabsContent value="curl" className="mt-4">
                            <CodeBlock
                              code={curlSync}
                              language="bash"
                              copied={copied === 'curl-sync'}
                              onCopy={() => copyToClipboard(curlSync, 'curl-sync')}
                              variant="dark"
                              collapsible
                              showWrapToggle
                            />
                          </TabsContent>
                        </Tabs>
                      </section>
                    </TabsContent>

                    <TabsContent value="async" className="mt-6 space-y-10">
                      <section id="async-endpoint" className="space-y-4 scroll-mt-24">
	                        <h3 className="text-2xl font-semibold">Асинхронный метод</h3>
                        <div className="space-y-3">
	                          <EndpointRow method="POST" path="/v1.0/extract-async" description="Запускает асинхронное извлечение и возвращает run_id, URL для WebSocket-подключения и токен." />
	                          <EndpointRow method="GET" path="/v1.0/runs/{run_id}" description="Опрос статуса и результата асинхронного запуска." />
	                          <EndpointRow method="WS" path="/ws/{run_id}?token=…" description="Потоковая передача статуса, прогресса и результата; токен привязан к конкретному запуску и действует недолго." />
                        </div>
                        <p className="text-[var(--site-ink)] text-sm">
	                          Необязательный заголовок:{' '}
                          <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">Idempotency-Key</code> для защиты от повторной отправки запроса в течение 24 часов.
                        </p>
                      </section>

                      <section id="async-request-body" className="space-y-3 scroll-mt-24">
                        <h3 className="text-2xl font-semibold">Тело запроса</h3>
                        <CodeBlock
                          code={asyncRequestBody}
                          language="json"
                          copied={copied === 'async-request'}
                          onCopy={() => copyToClipboard(asyncRequestBody, 'async-request')}
                          variant="light"
                          defaultWrap
                          showWrapToggle
                        />
                        <p className="text-[var(--site-ink)] text-sm">Токены для WebSocket-подключений действуют недолго, обычно около 5 минут.</p>
                      </section>

                      <section id="async-responses" className="space-y-4 scroll-mt-24">
                        <h3 className="text-2xl font-semibold">Ответы</h3>
                        <p className="text-[var(--site-ink)] text-sm">
                          В асинхронных статусах поле <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">error</code> появляется только для терминальных ошибок <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">failed</code> и <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">cancelled</code>; для queued, running и успешных/completed состояний оно равно <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">null</code> или отсутствует.
                        </p>
                        <Tabs value={asyncResponsesTab} onValueChange={(v) => setAsyncResponsesTab(v as typeof asyncResponsesTab)}>
                          <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="start">Старт (200)</TabsTrigger>
	                            <TabsTrigger value="status">Статус запуска (200)</TabsTrigger>
                          </TabsList>
                          <TabsContent value="start" className="mt-4">
                            <CodeBlock
                              code={asyncStartResponse}
                              language="json"
                              copied={copied === 'async-start'}
                              onCopy={() => copyToClipboard(asyncStartResponse, 'async-start')}
                              variant="light"
                              defaultWrap
                              showWrapToggle
                            />
                          </TabsContent>
                          <TabsContent value="status" className="mt-4">
                            <CodeBlock
                              code={asyncRunStatusResponse}
                              language="json"
                              copied={copied === 'async-status'}
                              onCopy={() => copyToClipboard(asyncRunStatusResponse, 'async-status')}
                              variant="light"
                              defaultWrap
                              showWrapToggle
                            />
                          </TabsContent>
                        </Tabs>
                      </section>

                      <section id="async-websocket" className="space-y-3 scroll-mt-24">
                        <h3 className="text-2xl font-semibold">WebSocket</h3>
                        <p className="text-[var(--site-ink)]">
                          Подключайтесь к адресу <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">websocket</code>, который приходит из{' '}
	                          <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">/v1.0/extract-async</code>. Сервер сразу отправляет снимок текущего статуса.
                        </p>
                        <CodeBlock
                          code={wsMessageTypes}
                          language="json"
                          copied={copied === 'ws-types'}
                          onCopy={() => copyToClipboard(wsMessageTypes, 'ws-types')}
                          variant="light"
                          defaultWrap
                          showWrapToggle
                        />
                      </section>

                      <section id="async-examples" className="space-y-4 scroll-mt-24">
                        <h3 className="text-2xl font-semibold">Примеры кода</h3>

                        <Tabs value={asyncExamplesTab} onValueChange={(v) => setAsyncExamplesTab(v as typeof asyncExamplesTab)}>
                          <TabsList className="hidden sm:grid w-full grid-cols-3">
                            <TabsTrigger value="start">Старт (cURL)</TabsTrigger>
                            <TabsTrigger value="poll">Опрос (cURL)</TabsTrigger>
                            <TabsTrigger value="websocket">WebSocket (JS)</TabsTrigger>
                          </TabsList>

                          <div className="sm:hidden">
                            <NativeSelect
                              value={asyncExamplesTab}
                              onValueChange={(value) => setAsyncExamplesTab(value as typeof asyncExamplesTab)}
                              ariaLabel="Выбрать пример асинхронного запроса"
                              options={[
                                { value: 'start', label: 'Старт (cURL)' },
                                { value: 'poll', label: 'Опрос (cURL)' },
                                { value: 'websocket', label: 'WebSocket (JS)' },
                              ]}
                            />
                          </div>

                          <TabsContent value="start" className="mt-4">
                            <CodeBlock
                              code={curlAsyncStart}
                              language="bash"
                              copied={copied === 'curl-async'}
                              onCopy={() => copyToClipboard(curlAsyncStart, 'curl-async')}
                              variant="dark"
                              collapsible
                              showWrapToggle
                            />
                          </TabsContent>

                          <TabsContent value="poll" className="mt-4">
                            <CodeBlock
                              code={curlAsyncPoll}
                              language="bash"
                              copied={copied === 'curl-async-poll'}
                              onCopy={() => copyToClipboard(curlAsyncPoll, 'curl-async-poll')}
                              variant="dark"
                              collapsible
                              showWrapToggle
                            />
                          </TabsContent>

                          <TabsContent value="websocket" className="mt-4">
                            <CodeBlock
                              code={wsExample}
                              language="javascript"
                              copied={copied === 'ws'}
                              onCopy={() => copyToClipboard(wsExample, 'ws')}
                              variant="dark"
                              collapsible
                              showWrapToggle
                            />
                          </TabsContent>
                        </Tabs>
                      </section>
                    </TabsContent>
                  </Tabs>
                </section>

                <section id="limits" className="space-y-3 scroll-mt-24">
	                  <h2 className="text-2xl font-semibold">Лимиты и оплата</h2>
                  <ul className="list-disc list-inside space-y-1 text-[var(--site-ink)]">
                    <li>Лимит параллельных запусков по тарифам: Хобби 1, Базовый 5, Про 20, Индивидуальный по договору.</li>
                    <li>
                      Когда исчерпан лимит параллельных запусков, сервис возвращает 429, включая{' '}
                      <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">Retry-After: 1</code>.
                    </li>
                    <li>
                      Тарифицируемые запросы:{' '}
                      <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">POST /v1.0/extract</code> и{' '}
                      <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">POST /v1.0/extract-async</code>; тарификация происходит в момент принятия запроса.
                    </li>
	                    <li>Опрос асинхронного статуса и сообщения по WebSocket не тарифицируются.</li>
	                    <li>Таймаут по умолчанию 30 с, максимум 120 с, длина поля <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">prompt</code> до 2048 символов.</li>
                  </ul>
                </section>

                <div className="pt-2">
                  <Button variant="link" className="px-0" onClick={navigateTo('top')}>
                    Наверх
                  </Button>
                </div>
          </div>
        </AnimatedContainer>
      </PageSection>
    </>
  );
}

function CopyField({
  label,
  value,
  copied,
  onCopy,
}: {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="rounded-md border bg-[var(--site-surface-inset)] p-3 flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="text-xs font-medium text-[var(--site-ink)]">{label}</div>
        <code className="text-sm break-all">{value}</code>
      </div>
      <button onClick={onCopy} className="shrink-0 p-1 hover:bg-[color:rgba(23,38,59,0.12)] rounded" aria-label={`Скопировать ${label}`}>
        {copied ? <CheckIcon className="h-4 w-4 text-green-600" /> : <CopyIcon className="h-4 w-4" />}
      </button>
    </div>
  );
}

function EndpointRow({ method, path, description }: { method: string; path: string; description: string }) {
  const badgeClass =
    method === 'POST'
      ? 'bg-green-100 text-green-800'
      : method === 'GET'
        ? 'bg-blue-100 text-blue-800'
        : 'bg-purple-100 text-purple-800';

  return (
    <div className="flex flex-col gap-2 border border-[color:var(--site-border-soft)] rounded-md p-3">
      <div className="flex items-center gap-3">
        <span className={`${badgeClass} px-2 py-1 rounded text-sm font-medium`}>{method}</span>
        <code className="text-sm font-semibold">{path}</code>
      </div>
      <p className="text-[var(--site-ink)] text-sm">{description}</p>
    </div>
  );
}

type PlaygroundResult = {
  upstreamOk: boolean;
  upstreamStatus: number;
  durationMs: number;
  idempotencyKey?: string;
  headers?: Record<string, string | null>;
  bodyIsJson: boolean;
  body: unknown;
  error?: string;
};
function ApiPlayground({
  apiBaseUrl,
  isAuthenticated,
  copied,
  copyToClipboard,
}: {
  apiBaseUrl: string;
  isAuthenticated: boolean;
  copied: string | null;
  copyToClipboard: (text: string, type: string) => void;
}) {
  const [url, setUrl] = useState('https://www.example.com/product/1234');
  const [prompt, setPrompt] = useState('technical specifications');
  const [waitMs, setWaitMs] = useState<number>(0);
  const [waitUntil, setWaitUntil] = useState<'load' | 'domcontentloaded' | 'networkidle'>('load');
  const [waitForSelector, setWaitForSelector] = useState<string>('#price');
  const [timeoutMs, setTimeoutMs] = useState<number>(PLAYGROUND_DEFAULT_TIMEOUT_MS);
  const [timeoutManuallyEdited, setTimeoutManuallyEdited] = useState<boolean>(false);
  const [screenshot, setScreenshot] = useState<boolean>(false);
  const [advanced, setAdvanced] = useState(false);
  const [schemaId, setSchemaId] = useState<string>('');
  const [schemaText, setSchemaText] = useState<string>('');
  const [enforceSchema, setEnforceSchema] = useState<boolean>(false);
  const [language, setLanguage] = useState<string>('en');
  const [idempotencyKey, setIdempotencyKey] = useState<string>(DEFAULT_IDEMPOTENCY_KEY);
  const [overrideKeyEnabled, setOverrideKeyEnabled] = useState<boolean>(false);
  const [apiKey, setApiKey] = useState<string>('');
  const [running, setRunning] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [result, setResult] = useState<PlaygroundResult | null>(null);
  const [pollResult, setPollResult] = useState<PlaygroundResult | null>(null);
  const [snippetTab, setSnippetTab] = useState<'curl' | 'javascript' | 'python'>('curl');
  const lastPayloadSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    setTimeoutMs((current) => {
      if (!timeoutManuallyEdited) {
        return PLAYGROUND_DEFAULT_TIMEOUT_MS;
      }
      const bounded = clampPlaygroundTimeoutMs(current, PLAYGROUND_MAX_TIMEOUT_MS);
      return bounded ?? PLAYGROUND_DEFAULT_TIMEOUT_MS;
    });
  }, [timeoutManuallyEdited]);

  useEffect(() => {
    setIdempotencyKey((current) => (current === DEFAULT_IDEMPOTENCY_KEY ? createIdempotencyKey() : current));
  }, []);

  const payload = useMemo(() => {
    const options: Record<string, unknown> = {};
    const boundedTimeoutMs = clampPlaygroundTimeoutMs(timeoutMs, PLAYGROUND_MAX_TIMEOUT_MS);
    if (boundedTimeoutMs !== null) options.timeout_ms = boundedTimeoutMs;
    if (screenshot) options.screenshot = true;
    if (Number.isFinite(waitMs) && waitMs > 0) options.wait_ms = Math.round(waitMs);
    if (waitUntil) options.wait_until = waitUntil;
    if (waitForSelector.trim()) options.wait_for_selector = waitForSelector.trim();

    const body: Record<string, unknown> = {
      url: url.trim(),
      prompt: prompt.trim(),
      options,
    };

    if (advanced) {
      const lang = language.trim();
      if (lang) body.language = lang;

      const schemaIdValue = schemaId.trim();
      if (schemaIdValue) body.schema_id = schemaIdValue;

      if (schemaText.trim()) {
        try {
          body.schema = JSON.parse(schemaText);
          if (schemaIdValue || schemaText.trim()) body.enforce_schema = Boolean(enforceSchema);
        } catch {
          // validated on run
        }
      } else if (schemaIdValue) {
        body.enforce_schema = Boolean(enforceSchema);
      }
    }

    return body;
  }, [
    advanced,
    enforceSchema,
    language,
    prompt,
    schemaId,
    schemaText,
    screenshot,
    timeoutMs,
    url,
    waitForSelector,
    waitMs,
    waitUntil,
  ]);

  const payloadSignature = useMemo(() => JSON.stringify({ mode: 'async', payload }), [payload]);

  useEffect(() => {
    if (lastPayloadSignatureRef.current === null) {
      lastPayloadSignatureRef.current = payloadSignature;
      return;
    }

    if (lastPayloadSignatureRef.current !== payloadSignature) {
      lastPayloadSignatureRef.current = payloadSignature;
      setIdempotencyKey(createIdempotencyKey());
    }
  }, [payloadSignature]);

  const requestJson = useMemo(() => JSON.stringify(payload, null, 2), [payload]);
  const asyncEndpoint = `${apiBaseUrl}/v1.0/extract-async`;

  const curlSnippet = useMemo(
    () =>
      `curl -X POST ${asyncEndpoint} \\\n  -H 'Authorization: Bearer $API_KEY' \\\n  -H 'Content-Type: application/json' \\\n  -H 'Idempotency-Key: ${idempotencyKey}' \\\n  -d @- <<'JSON'\n${requestJson}\nJSON`,
    [asyncEndpoint, idempotencyKey, requestJson],
  );

  const javascriptSnippet = useMemo(
    () =>
      `const res = await fetch('${asyncEndpoint}', {\n  method: 'POST',\n  headers: {\n    'Authorization': 'Bearer ' + process.env.API_KEY,\n    'Content-Type': 'application/json',\n    'Idempotency-Key': '${idempotencyKey}',\n  },\n  body: JSON.stringify(${requestJson}),\n});\n\nconst data = await res.json();\nconsole.log(res.status, data);`,
    [asyncEndpoint, idempotencyKey, requestJson],
  );

  const pythonSnippetTimeoutSeconds = useMemo(
    () => Math.ceil(resolvePlaygroundUpstreamTimeoutMs('async', payload) / 1000),
    [payload],
  );

  const pythonSnippet = useMemo(
    () =>
      `import os, requests\n\npayload = ${requestJson}\n\nresp = requests.post(\n  '${asyncEndpoint}',\n  headers={\n    'Authorization': f\"Bearer {os.environ['API_KEY']}\",\n    'Content-Type': 'application/json',\n    'Idempotency-Key': '${idempotencyKey}',\n  },\n  json=payload,\n  timeout=${pythonSnippetTimeoutSeconds},\n)\n\nprint(resp.status_code, resp.json())`,
    [asyncEndpoint, idempotencyKey, pythonSnippetTimeoutSeconds, requestJson],
  );

  const runId = useMemo(() => {
    if (!result?.bodyIsJson) return null;
    const body = result.body as any;
    return typeof body?.run_id === 'string' ? body.run_id : null;
  }, [result]);

  const pollCurlSnippet = useMemo(() => {
    if (!runId) return null;
    return `curl -X GET ${apiBaseUrl}/v1.0/runs/${runId} \\\n  -H 'Authorization: Bearer $API_KEY'`;
  }, [apiBaseUrl, runId]);

  const validateBeforeRun = () => {
    if (!payload.url) return 'Нужен URL';
    if (!payload.prompt) return 'Поля url и prompt обязательны';
    if (advanced && schemaText.trim()) {
      try {
        JSON.parse(schemaText);
      } catch {
        return 'Поле schema должно содержать корректный JSON';
      }
    }
    if (!isAuthenticated) {
      const key = apiKey.trim();
      if (!key) return 'Требуется API-ключ или вход в аккаунт';
    } else if (overrideKeyEnabled && !apiKey.trim()) {
      return 'Включена подмена API-ключа, но поле ключа пустое';
    }
    return null;
  };

  const run = async () => {
    const validationError = validateBeforeRun();
    if (validationError) {
      setError(validationError);
      setStatusMessage(validationError);
      return;
    }

    setRunning(true);
    setError(null);
    setPollResult(null);
    setStatusMessage('Отправка запроса в песочницу…');

    try {
      const response = await fetch('/api/docs/playground', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'async',
          payload,
          idempotencyKey,
          apiKey: isAuthenticated ? (overrideKeyEnabled ? apiKey.trim() : undefined) : apiKey.trim(),
        }),
      });

      const data = (await response.json()) as PlaygroundResult & { error?: string };
      if (!response.ok) {
        const message = data?.error || `Запрос завершился ошибкой (${response.status})`;
        setError(message);
        setStatusMessage(message);
        setResult(null);
        return;
      }

      if (typeof data?.idempotencyKey === 'string' && data.idempotencyKey) {
        setIdempotencyKey(data.idempotencyKey);
      }

      setResult(data);
      setStatusMessage(`Запрос выполнен. HTTP ${data.upstreamStatus}.`);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Запрос завершился ошибкой';
      setError(message);
      setStatusMessage(message);
      setResult(null);
    } finally {
      setRunning(false);
    }
  };

  const poll = async () => {
    if (!runId) return;
    setRunning(true);
    setError(null);
    setStatusMessage('Проверяем статус запуска…');

    try {
      const response = await fetch('/api/docs/playground', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'poll',
          runId,
          apiKey: isAuthenticated ? (overrideKeyEnabled ? apiKey.trim() : undefined) : apiKey.trim(),
        }),
      });

      const data = (await response.json()) as PlaygroundResult & { error?: string };
      if (!response.ok) {
        const message = data?.error || `Запрос завершился ошибкой (${response.status})`;
        setError(message);
        setStatusMessage(message);
        setPollResult(null);
        return;
      }

      setPollResult(data);
      setStatusMessage(`Статус запуска обновлен. HTTP ${data.upstreamStatus}.`);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Опрос завершился ошибкой';
      setError(message);
      setStatusMessage(message);
      setPollResult(null);
    } finally {
      setRunning(false);
    }
  };

  const responseBodyString = useMemo(() => {
    if (!result) return '';
    if (result.bodyIsJson) return JSON.stringify(result.body, null, 2);
    return typeof result.body === 'string' ? result.body : JSON.stringify(result.body, null, 2);
  }, [result]);

  const pollBodyString = useMemo(() => {
    if (!pollResult) return '';
    if (pollResult.bodyIsJson) return JSON.stringify(pollResult.body, null, 2);
    return typeof pollResult.body === 'string' ? pollResult.body : JSON.stringify(pollResult.body, null, 2);
  }, [pollResult]);

  return (
    <Card>
      <CardHeader className="pb-4 space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle>Публичная песочница `extract-async`</CardTitle>
          <SectionPill>Interactive mode: async only</SectionPill>
        </div>
        <p className="text-sm text-[var(--site-ink)]">
          Запуски идут через прокси на стороне сервера, чтобы избежать CORS и не держать long-running HTTP-запросы на `www.s-shot.ru`. Ваш API-ключ никогда не показывается на этой странице.
        </p>
      </CardHeader>

      <CardContent className="space-y-6" aria-busy={running}>
        <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {statusMessage}
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            {!isAuthenticated && (
              <div className="space-y-2">
                <Label htmlFor="playground-api-key">API-ключ</Label>
                <Input
                  id="playground-api-key"
                  type="password"
                  placeholder="ws_…"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  autoComplete="off"
                />
              </div>
            )}

            {isAuthenticated && (
              <div className="rounded-md border bg-[var(--site-surface-inset)] p-3 text-sm text-[var(--site-ink)] space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium">Использовать другой API-ключ (необязательно)</div>
                  <Switch
                    aria-label="Использовать другой API-ключ"
                    checked={overrideKeyEnabled}
                    onCheckedChange={(v) => setOverrideKeyEnabled(Boolean(v))}
                  />
                </div>
                {overrideKeyEnabled && (
                  <Input
                    type="password"
                    placeholder="ws_…"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    autoComplete="off"
                  />
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="playground-url">Целевой URL</Label>
              <Input id="playground-url" value={url} onChange={(e) => setUrl(e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="playground-prompt">Что извлечь</Label>
              <Textarea
                id="playground-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="min-h-[92px]"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>wait_until</Label>
                <NativeSelect
                  value={waitUntil}
                  onValueChange={(value) => setWaitUntil(value as typeof waitUntil)}
                  ariaLabel="Выбрать значение wait_until"
                  options={[
                    { value: 'networkidle', label: 'networkidle' },
                    { value: 'domcontentloaded', label: 'domcontentloaded' },
                    { value: 'load', label: 'load' },
                  ]}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="playground-wait-ms">wait_ms</Label>
                <Input
                  id="playground-wait-ms"
                  type="number"
                  min={0}
                  max={60000}
                  value={waitMs}
                  onChange={(e) => setWaitMs(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="playground-wait-for">wait_for_selector</Label>
                <Input
                  id="playground-wait-for"
                  value={waitForSelector}
                  onChange={(e) => setWaitForSelector(e.target.value)}
                  placeholder="#price"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="playground-timeout-ms">timeout_ms</Label>
                <Input
                  id="playground-timeout-ms"
                  type="number"
                  min={1}
                  max={PLAYGROUND_MAX_TIMEOUT_MS}
                  value={timeoutMs}
                  onChange={(e) => {
                    setTimeoutManuallyEdited(true);
                    setTimeoutMs(Number(e.target.value));
                  }}
                />
              </div>
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="space-y-0.5">
                <div className="text-sm font-medium">Скриншот</div>
                <div className="text-xs text-[var(--site-ink)]">Добавляет поле <code>screenshot_url</code>, когда снимок доступен.</div>
              </div>
              <Switch
                aria-label="Добавить скриншот"
                checked={screenshot}
                onCheckedChange={(v) => setScreenshot(Boolean(v))}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
              <div className="space-y-2">
                <Label htmlFor="playground-idem">Idempotency-Key</Label>
                <Input id="playground-idem" value={idempotencyKey} onChange={(e) => setIdempotencyKey(e.target.value)} />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIdempotencyKey(createIdempotencyKey())}
                className="w-full sm:w-auto"
              >
                Сгенерировать заново
              </Button>
            </div>

            <div className="rounded-md border p-3 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="space-y-0.5">
                  <div className="text-sm font-medium">Расширенные параметры</div>
                  <div className="text-xs text-[var(--site-ink)]">Необязательные поля <code>schema_id</code>, <code>schema</code> и <code>language</code>.</div>
                </div>
                <Switch
                  aria-label="Показать расширенные параметры"
                  checked={advanced}
                  onCheckedChange={(v) => setAdvanced(Boolean(v))}
                />
              </div>

              {advanced && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="playground-language">language</Label>
                    <Input
                      id="playground-language"
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      placeholder="en"
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-md border p-3">
                    <div className="space-y-0.5">
                      <div className="text-sm font-medium">enforce_schema</div>
                      <div className="text-xs text-[var(--site-ink)]">Завершить запрос ошибкой, если результат не соответствует <code>schema</code>.</div>
                    </div>
                    <Switch
                      aria-label="Включить enforce_schema"
                      checked={enforceSchema}
                      onCheckedChange={(v) => setEnforceSchema(Boolean(v))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="playground-schema-id">schema_id</Label>
                    <Input
                      id="playground-schema-id"
                      value={schemaId}
                      onChange={(e) => setSchemaId(e.target.value)}
                      placeholder="schema_abc"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="playground-schema">schema (JSON Schema)</Label>
                    <Textarea
                      id="playground-schema"
                      value={schemaText}
                      onChange={(e) => setSchemaText(e.target.value)}
                      placeholder='{"type":"object","properties":{"name":{"type":"string"}}}'
                      className="min-h-[120px] font-mono text-xs"
                    />
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {error}
              </div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button onClick={run} disabled={running} className="sm:min-w-[140px]">
                {running ? 'Выполняется…' : 'Запустить асинхронный запрос'}
              </Button>
              {runId && (
                <Button variant="outline" onClick={poll} disabled={running}>
                  Проверить статус
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-semibold text-[var(--site-ink-strong)]">JSON запроса</div>
              </div>
              <CodeBlock
                code={requestJson}
                language="json"
                copied={copied === 'playground-request'}
                onCopy={() => copyToClipboard(requestJson, 'playground-request')}
                variant="light"
                defaultWrap
                showWrapToggle
                collapsible
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-semibold text-[var(--site-ink-strong)]">Сгенерированный код</div>
              </div>

              <Tabs value={snippetTab} onValueChange={(v) => setSnippetTab(v as typeof snippetTab)}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="curl">cURL</TabsTrigger>
                  <TabsTrigger value="javascript">JS</TabsTrigger>
                  <TabsTrigger value="python">Python</TabsTrigger>
                </TabsList>
                <TabsContent value="curl" className="mt-3">
                  <CodeBlock
                    code={curlSnippet}
                    language="bash"
                    copied={copied === 'playground-curl'}
                    onCopy={() => copyToClipboard(curlSnippet, 'playground-curl')}
                    variant="light"
                    defaultWrap
                    collapsible
                    showWrapToggle
                  />
                  {pollCurlSnippet && (
                    <div className="mt-3">
                      <div className="text-xs font-medium text-[var(--site-ink)] mb-2">Проверка статуса:</div>
                      <CodeBlock
                        code={pollCurlSnippet}
                        language="bash"
                        copied={copied === 'playground-curl-poll'}
                        onCopy={() => copyToClipboard(pollCurlSnippet, 'playground-curl-poll')}
                        variant="light"
                        defaultWrap
                        showWrapToggle
                      />
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="javascript" className="mt-3">
                  <CodeBlock
                    code={javascriptSnippet}
                    language="javascript"
                    copied={copied === 'playground-js'}
                    onCopy={() => copyToClipboard(javascriptSnippet, 'playground-js')}
                    variant="light"
                    defaultWrap
                    collapsible
                    showWrapToggle
                  />
                </TabsContent>
                <TabsContent value="python" className="mt-3">
                  <CodeBlock
                    code={pythonSnippet}
                    language="python"
                    copied={copied === 'playground-py'}
                    onCopy={() => copyToClipboard(pythonSnippet, 'playground-py')}
                    variant="light"
                    defaultWrap
                    collapsible
                    showWrapToggle
                  />
                </TabsContent>
              </Tabs>
            </div>

            <div className="space-y-2">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm font-semibold text-[var(--site-ink-strong)]">Ответ</div>
                {result && (
                  <div className="text-xs text-[var(--site-ink)]" role="status" aria-live="polite" aria-atomic="true">
                    Статус {result.upstreamStatus} · {result.durationMs} мс ·{' '}
                    <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">{result.headers?.['x-request-id'] ?? 'x-request-id: —'}</code>
                  </div>
                )}
              </div>
              {!result ? (
                <div className="rounded-md border bg-[var(--site-surface-inset)] p-3 text-sm text-[var(--site-ink)]">Запустите запрос, чтобы увидеть ответ здесь.</div>
              ) : (
                <div className="space-y-2">
                  {!result.upstreamOk && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                      API вернул статус {result.upstreamStatus}. Подробности смотрите в теле ответа.
                    </div>
                  )}
                  <CodeBlock
                    code={responseBodyString || '(пусто)'}
                    language={result.bodyIsJson ? 'json' : 'text'}
                    copied={copied === 'playground-response'}
                    onCopy={() => copyToClipboard(responseBodyString, 'playground-response')}
                    variant="light"
                    defaultWrap
                    showWrapToggle
                    collapsible
                  />
                </div>
              )}
            </div>

            {pollResult && (
              <div className="space-y-2">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm font-semibold text-[var(--site-ink-strong)]">Результат опроса</div>
                  <div className="text-xs text-[var(--site-ink)]" role="status" aria-live="polite" aria-atomic="true">
                    Статус {pollResult.upstreamStatus} · {pollResult.durationMs} мс
                  </div>
                </div>
                <div className="space-y-2">
                  {!pollResult.upstreamOk && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                      API вернул статус {pollResult.upstreamStatus}. Подробности смотрите в теле ответа.
                    </div>
                  )}
                  <CodeBlock
                    code={pollBodyString || '(пусто)'}
                    language={pollResult.bodyIsJson ? 'json' : 'text'}
                    copied={copied === 'playground-poll'}
                    onCopy={() => copyToClipboard(pollBodyString, 'playground-poll')}
                    variant="light"
                    defaultWrap
                    showWrapToggle
                    collapsible
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
